import {
  ALEO_CHAINS,
  EVMChainConfigs,
  EVMChainInfo,
  EVMChainType,
  EVM_CHAINS,
  NETWORK_TYPES,
  SupportedChainsType,
  tNetworkTypes
} from '@/interfaces/bridge/chain';
import {
  VITE_ALEO_BRIDGE_CHAIN_ID,
  VITE_ARBITRUM_BRIDGE_CHAIN_ID,
  VITE_BASE_BRIDGE_CHAIN_ID,
  VITE_ETHEREUM_BRIDGE_CHAIN_ID,
  VITE_BSC_BRIDGE_CHAIN_ID,
  BRIDGE_NETWORK_TYPE,
  VITE_ARBITRUM_PREDICATE_SERVICE_ADDRESS,
  VITE_BASE_PREDICATE_SERVICE_ADDRESS,
  VITE_ETHEREUM_PREDICATE_SERVICE_ADDRESS,
  VITE_BSC_PREDICATE_SERVICE_ADDRESS,
  VITE_ETHEREUM_RPC_URL,
  VITE_ARBITRUM_RPC_URL,
  VITE_BSC_RPC_URL,
  VITE_BASE_RPC_URL
} from './bridgeEnv';

const BROWSER_UNSAFE_RPC_HOSTS = new Set(['rpc.fullsend.to']);

const resolveRpcUrl = (rpcUrl: string | undefined, fallbackUrl: string) => {
  if (!rpcUrl) return fallbackUrl;

  try {
    const parsed = new URL(rpcUrl);
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return fallbackUrl;
    }

    if (BROWSER_UNSAFE_RPC_HOSTS.has(parsed.hostname)) {
      return fallbackUrl;
    }

    return rpcUrl;
  } catch {
    return fallbackUrl;
  }
};

const EVM_CHAINS_CONFIG: EVMChainConfigs = {
  [EVM_CHAINS.ETHEREUM]: {
    [NETWORK_TYPES.MAINNET]: {
      name: 'Ethereum Mainnet',
      rpcUrl: resolveRpcUrl(VITE_ETHEREUM_RPC_URL, 'https://eth.llamarpc.com'),
      chainId: 1,
      currency: 'ETH',
      shortName: 'Ethereum',
      explorerUrl: 'https://etherscan.io/'
    },
    [NETWORK_TYPES.TESTNET]: {
      name: 'Sepolia Test Network',
      rpcUrl: 'https://1rpc.io/sepolia',
      chainId: 11155111,
      currency: 'ETH',
      shortName: 'Ethereum',
      explorerUrl: 'https://sepolia.etherscan.io/'
    }
  },
  [EVM_CHAINS.BASE]: {
    [NETWORK_TYPES.MAINNET]: {
      name: 'Base Mainnet',
      rpcUrl: resolveRpcUrl(VITE_BASE_RPC_URL, 'https://mainnet.base.org'),
      chainId: 8453,
      currency: 'ETH',
      explorerUrl: 'https://basescan.org/',
      shortName: 'Base'
    },
    [NETWORK_TYPES.TESTNET]: {
      name: 'Base Sepolia Testnet',
      rpcUrl: 'https://base-sepolia-rpc.publicnode.com',
      chainId: 84532,
      currency: 'ETH',
      shortName: 'Base Goerli',
      explorerUrl: 'https://sepolia.basescan.org/'
    }
  },
  [EVM_CHAINS.ARBITRUM]: {
    [NETWORK_TYPES.MAINNET]: {
      name: 'Arbitrum One',
      rpcUrl: resolveRpcUrl(VITE_ARBITRUM_RPC_URL, 'https://arb1.arbitrum.io/rpc'),
      chainId: 42161,
      currency: 'ETH',
      explorerUrl: 'https://arbiscan.io/',
      shortName: 'Arbitrum'
    },
    [NETWORK_TYPES.TESTNET]: {
      name: 'Arbitrum Sepolia Testnet',
      rpcUrl: 'https://arbitrum-sepolia-rpc.publicnode.com',
      chainId: 421614,
      currency: 'ETH',
      shortName: 'Arbitrum Sepolia',
      explorerUrl: 'https://sepolia.arbiscan.io/'
    }
  },
  [EVM_CHAINS.BSC]: {
    [NETWORK_TYPES.MAINNET]: {
      name: 'BNB Smart Chain',
      rpcUrl: resolveRpcUrl(VITE_BSC_RPC_URL, 'https://bsc-dataseed.binance.org'),
      chainId: 56,
      currency: 'BNB',
      shortName: 'BSC',
      explorerUrl: 'https://bscscan.com/'
    },
    [NETWORK_TYPES.TESTNET]: {
      name: 'BNB Smart Chain Testnet',
      rpcUrl: 'https://bsc-testnet-rpc.publicnode.com',
      chainId: 97,
      currency: 'tBNB',
      shortName: 'BSC Testnet',
      explorerUrl: 'https://testnet.bscscan.com/'
    }
  }
};

export const getChainIdMappingByNetwork = (network: tNetworkTypes) => {
  const chainIdMapping: Record<string, number> = {};
  Object.entries(EVM_CHAINS_CONFIG).forEach(([chain, networkConfig]) => {
    const config = networkConfig[network];
    if (config) {
      chainIdMapping[chain] = config.chainId;
    }
  });
  return chainIdMapping;
};

export const getEVMChainConfig = (chain: EVMChainType, network: tNetworkTypes = NETWORK_TYPES.TESTNET) => {
  return EVM_CHAINS_CONFIG[chain][network];
};

export const getAllL1ChainConfigList = (network: tNetworkTypes = NETWORK_TYPES.TESTNET) => {
  return Object.values(EVM_CHAINS).map((chain) => EVM_CHAINS_CONFIG[chain][network]);
};

export const getAllMainNetChainInfo = () => {
  return Object.values(EVM_CHAINS).map((chain) => EVM_CHAINS_CONFIG[chain][NETWORK_TYPES.MAINNET]);
};

export const BRIDGE_CHAIN_ID_MAPPING: Record<SupportedChainsType, string> = {
  [EVM_CHAINS.ETHEREUM]: VITE_ETHEREUM_BRIDGE_CHAIN_ID,
  [ALEO_CHAINS.ALEO]: VITE_ALEO_BRIDGE_CHAIN_ID,
  [EVM_CHAINS.BASE]: VITE_BASE_BRIDGE_CHAIN_ID,
  [EVM_CHAINS.ARBITRUM]: VITE_ARBITRUM_BRIDGE_CHAIN_ID,
  [EVM_CHAINS.BSC]: VITE_BSC_BRIDGE_CHAIN_ID
} as const;

export const getBridgeChainId = (chain: SupportedChainsType) => {
  return BRIDGE_CHAIN_ID_MAPPING[chain];
};

export const getChainFromBridgeChainId = (bridgeChainId: string) => {
  const chainIds = Object.values(BRIDGE_CHAIN_ID_MAPPING);
  const index = chainIds.indexOf(bridgeChainId);
  if (index === -1) {
    return Object.keys(BRIDGE_CHAIN_ID_MAPPING)[0] as SupportedChainsType;
  } else return Object.keys(BRIDGE_CHAIN_ID_MAPPING)[index] as SupportedChainsType;
};

export const getBridgeChainIdFromChainId = (chainId: number, network?: tNetworkTypes) => {
  let bridgeChainId: string | undefined;
  Object.entries(BRIDGE_CHAIN_ID_MAPPING).forEach(([chain, bridgeId]) => {
    const chainConfig = EVM_CHAINS_CONFIG[chain as EVMChainType]?.[network || NETWORK_TYPES.TESTNET];
    if (chainConfig && chainConfig.chainId === chainId) {
      bridgeChainId = bridgeId;
    }
  });
  if (!bridgeChainId) {
    return '';
  }
  return bridgeChainId;
};

export const getChainIdfromChainName = (chainName: SupportedChainsType) => {
  const chainConfig = EVM_CHAINS_CONFIG[chainName as EVMChainType];
  if (!chainConfig) {
    return null;
  }
  const network = BRIDGE_NETWORK_TYPE as tNetworkTypes;
  return chainConfig[network]?.chainId || null;
};

export const getChainIdFromBridgeChainId = (bridgeChainId: string) => {
  const chain = Object.entries(BRIDGE_CHAIN_ID_MAPPING).find(([, id]) => id === bridgeChainId)?.[0];
  const chainId = getChainIdfromChainName(chain as SupportedChainsType);
  return chainId;
};

export const getEVMChainInfoFromChain = (chain: SupportedChainsType): EVMChainInfo | null => {
  const chainConfig = EVM_CHAINS_CONFIG[chain as EVMChainType];
  if (!chainConfig) {
    return null;
  }
  const network = BRIDGE_NETWORK_TYPE as tNetworkTypes;
  return chainConfig[network] || null;
};

import type { BridgeImageNames } from '@/components/common/images';

interface IChainUIMappings {
  name: string;
  symbol: string;
  icon: BridgeImageNames;
}

export const ChainUiMapping: Record<SupportedChainsType, IChainUIMappings> = {
  [EVM_CHAINS.ETHEREUM]: { name: 'Ethereum', symbol: 'ETH', icon: 'ethereum' },
  [EVM_CHAINS.BASE]: { name: 'Base', symbol: 'ETH', icon: 'base' },
  [EVM_CHAINS.ARBITRUM]: { name: 'Arbitrum', symbol: 'ETH', icon: 'arbitrum' },
  [EVM_CHAINS.BSC]: { name: 'BSC', symbol: 'BNB', icon: 'bsc' },
  [ALEO_CHAINS.ALEO]: { name: 'Aleo', symbol: 'ALEO', icon: 'aleo' }
};

export const getChainUIMapping = (chain: SupportedChainsType): IChainUIMappings => {
  const mapping = ChainUiMapping[chain];
  if (!mapping) return { name: '', symbol: '', icon: 'ethereum' };
  return mapping;
};

export const getChainDetailsByChainID = (chainId: string) => {
  const chain = getChainFromBridgeChainId(chainId);
  const UIMapping = getChainUIMapping(chain);
  return { chain, ...UIMapping };
};

// Map bridge network type to the correct Aleo wallet adapter network
export const ALEO_WALLET_ADAPTER = BRIDGE_NETWORK_TYPE === NETWORK_TYPES.MAINNET ? 'mainnet' : 'testnet';

export const getChainNameFromChainId = (chainId: number): SupportedChainsType | null => {
  const chainEntries = Object.entries(EVM_CHAINS_CONFIG);
  for (const [chainName, config] of chainEntries) {
    const networkConfig = config[BRIDGE_NETWORK_TYPE as tNetworkTypes];
    if (networkConfig && networkConfig.chainId === chainId) {
      return chainName as SupportedChainsType;
    }
  }
  return null;
};

export const isSupportedBridgeChainId = (bridgeChainId: string): boolean => {
  return Object.values(BRIDGE_CHAIN_ID_MAPPING).includes(bridgeChainId);
};

export const CHAIN_PREDICATE_SERVICE_ADDRESS: Record<EVMChainType, string> = {
  [EVM_CHAINS.ETHEREUM]: VITE_ETHEREUM_PREDICATE_SERVICE_ADDRESS,
  [EVM_CHAINS.BASE]: VITE_BASE_PREDICATE_SERVICE_ADDRESS,
  [EVM_CHAINS.ARBITRUM]: VITE_ARBITRUM_PREDICATE_SERVICE_ADDRESS,
  [EVM_CHAINS.BSC]: VITE_BSC_PREDICATE_SERVICE_ADDRESS
};

export const getChainFromChainID = (chainId: number): SupportedChainsType | null => {
  return getChainNameFromChainId(chainId);
};

export const getPredicateServiceAddressByChainId = (chainId: number): string => {
  const chain = getChainFromChainID(chainId);
  return CHAIN_PREDICATE_SERVICE_ADDRESS[chain as EVMChainType];
};

export const getRPCURLbyChain = (chain: EVMChainType): string | null => {
  const chainConfig = EVM_CHAINS_CONFIG[chain];
  if (!chainConfig) {
    return null;
  }
  const network = BRIDGE_NETWORK_TYPE as tNetworkTypes;
  return chainConfig[network]?.rpcUrl || null;
};
